// ============================================
// HELPERS - DIRTY STATE (SNAPSHOT) — COMPARTILHADO
// ============================================
// Helpers reaproveitados pelos modais de Tarefa/Subtarefa, Atividade, Caso e
// Subtarefa interna para detectar alterações reais no formulário (evitando
// falso positivo provocado pela reserialização do editor Quill na montagem).

/**
 * Remove tags HTML e normaliza espaços/entidades para comparar apenas o TEXTO
 * de um campo rico (Quill). HTML "vazio" (<p><br></p>, <p></p>, espaços) vira "".
 */
export const stripHtml = (html: string): string =>
  html
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/gi, " ")
    .replace(/\s+/g, " ")
    .trim();

/**
 * Normaliza um formData para comparação com o snapshot inicial:
 * undefined/null/"" são tratados como ausentes, descrição vazia normalizada
 * pelo Quill ("<p><br></p>") é ignorada e arrays vazios são descartados
 * (com os demais ordenados para comparação estável).
 *
 * @param data         objeto do formulário a normalizar
 * @param descricaoKeys campos textuais ricos (Quill) a reduzir via stripHtml
 * @param numericKeys   campos a coagir numericamente para comparação estável
 * @param horasKeys     campos de horas decimais a comparar em granularidade de
 *                      minutos (Math.round(valor*60)) — evita falso positivo pela
 *                      conversão lossy decimal↔HH:MM (ex.: 1.33 ↔ "01:20" ↔ 1.3333…)
 */
export const normalizeFormData = (
  data: Record<string, any>,
  descricaoKeys: string[] = ["descricao"],
  numericKeys: string[] = ["idReferencia", "idTipoAtividade"],
  horasKeys: string[] = ["estimativaHoras"]
): Record<string, any> => {
  const normalized: Record<string, any> = {};
  Object.entries(data).forEach(([key, value]) => {
    // Descrição (Quill/texto): comparar apenas o TEXTO normalizado (sem
    // tags/espaços), para que a reserialização do editor na montagem não
    // marque dirty. HTML "vazio" é tratado como ausente.
    if (descricaoKeys.includes(key)) {
      const texto = typeof value === "string" ? stripHtml(value) : "";
      if (texto === "") return;
      normalized[key] = texto;
      return;
    }
    if (value === undefined || value === null || value === "") return;
    // Horas decimais: comparar em minutos inteiros, absorvendo o ruído da
    // conversão decimal↔HH:MM que sujava o formulário só ao abrir o modal.
    if (horasKeys.includes(key)) {
      const n = Number(value);
      if (!Number.isNaN(n)) {
        normalized[key] = Math.round(n * 60);
        return;
      }
    }
    // Coerção numérica consistente entre snapshot e formData.
    if (numericKeys.includes(key)) {
      const n = Number(value);
      if (!Number.isNaN(n)) {
        normalized[key] = n;
        return;
      }
    }
    if (Array.isArray(value)) {
      if (value.length === 0) return;
      normalized[key] = [...value].sort();
      return;
    }
    normalized[key] = value;
  });
  return normalized;
};
